import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { AuthShell } from "@/components/shared/AuthShell";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";
import { professionSlugFromInvitePath } from "@/lib/profession-invite-path";
import { STRATEGY_REQUIREMENTS } from "@/pages/auth/professional-invite-steps";
import { AcceptProfessionalInvitationPage } from "./accept-professional-invitation-page";

/**
 * THE ONE ROUTE EVERY INVITATION ARRIVES ON, for any profession in the
 * registry.
 *
 * Adding a profession used to need a portal route named after it, so an
 * invitation generated for a registry entry nobody had hardcoded here landed
 * on the catch-all and redirected a real professional away from their own
 * signup. This resolves the invitation by TOKEN and renders the page its
 * verification strategy asks for.
 *
 * `fallback` is what a path that merely LOOKS like a single segment renders —
 * every typo, every stale link, every path this app has never had. It is the
 * same element the catch-all `path="*"` uses, and it has to be: this route is
 * a dynamic single-segment match, so it now outranks that catch-all for every
 * one-segment path in the app. Swallowing those into an error screen of our
 * own would be a visible regression (FM-2), which is why the behaviour is
 * passed in rather than reinvented here.
 */
export function ProfessionalInviteRoute({
  fallback,
}: {
  fallback: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const { professionInviteSlug, token: tokenFromPath } = useParams<{
    professionInviteSlug: string;
    token?: string;
  }>();
  const [searchParams] = useSearchParams();
  const { getProfessionalInvitationByToken } = useAuth();

  const slug =
    professionInviteSlug != null
      ? professionSlugFromInvitePath(professionInviteSlug)
      : null;
  /*
    THE QUERY STRING FIRST, because that is the shape of the link people
    actually receive: `professionalInviteLink` builds
    `/accept-${slug}-invitation?token=…`. The path form is an older second
    shape that still has to work.
  */
  const token = tokenFromPath ?? searchParams.get("token") ?? null;

  const [strategy, setStrategy] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    if (slug == null || token == null) {
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    getProfessionalInvitationByToken(token)
      .then((invitation) => {
        if (!cancelled) setStrategy(invitation.verificationStrategy);
      })
      /*
        Swallowed, and it has to be caught or the rejection is unhandled. An
        invitation that cannot be read leaves `strategy` null, which is the
        card below — the same thing the acceptance pages show for a token
        they cannot resolve.
      */
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, token, getProfessionalInvitationByToken]);

  // NOT an invitation path at all. Behaves exactly as it did before this
  // route existed.
  if (slug == null) {
    return <>{fallback}</>;
  }

  if (resolving) {
    /*
      THE SAME SHELL THE PAGE IT IS ABOUT TO RENDER USES. This state is the
      first thing an invited professional sees, and without the shell the
      brand mark and the language switcher appeared only once the page behind
      it resolved — a visible flash of a different, chromeless layout.
    */
    return (
      <AuthShell>
        <Card className="w-full shadow-sm">
          <CardContent className="flex flex-col items-center gap-3 p-4 sm:p-8">
            <LoadingLogo />
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  /*
    ONE PAGE FOR EVERY STRATEGY, composed from the verifications the
    profession requires (spec 42ec3c57, slice 3).

    THIS USED TO PICK BETWEEN TWO PAGES by the scalar strategy, which is
    exactly why a profession requiring TWO verifications could not be served:
    whichever page was chosen asked for one strategy's inputs, and the server
    then refused the applicant for the other's. The composed page asks for the
    union, so the choice this route used to make has no reason left.

    WHAT IT STILL DECIDES is whether this client can serve the invitation at
    all. A strategy the portal declares no requirements for would compose a
    wizard that asks for nothing, so it gets the unavailable card below rather
    than an empty flow. The page itself refuses the same case — it is reachable
    directly by URL — and this is the earlier of the two answers, before a
    loading shell has flashed.
  */
  if (strategy != null && strategy in STRATEGY_REQUIREMENTS) {
    return <AcceptProfessionalInvitationPage />;
  }

  /*
    A REAL INVITATION WE CANNOT FINISH HERE — an unreadable token, or a
    profession whose verification strategy this client declares no
    requirements for. NOT a redirect: bouncing a professional who was
    genuinely invited to "/" is the same defect as swallowing a typo, just
    pointed the other way.
  */
  return (
    <AuthShell>
      <Card className="w-full shadow-sm">
        <CardContent className="flex flex-col items-center gap-2 p-6 text-center sm:p-8">
          <h1 className="text-lg font-semibold">
            {t("auth.professionalInvite.unavailableTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("auth.professionalInvite.unavailableBody")}
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
